import { useState } from "react";
import { Paper, Typography, Box, Button } from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Loading from "../ui/Loading";
import CallsDetailModal from "./CallsDetailModal";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const CallsByProjectChart = ({
  data,
  isLoading,
  error,
  filters,
  callsDetail,
  getCalls,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  if (isLoading) return <Loading />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!data || data.length === 0)
    return <Typography>No data available</Typography>;

  // Show top 15 only
  const displayData = data.slice(0, 15);
  const chartHeight = Math.max(460, displayData.length * 34);

  const labels = displayData.map((item) => item.projectName || "Unknown");
  const futureData = displayData.map(
    (item) => item.countsByStatus?.future || 0,
  );
  const completeData = displayData.map(
    (item) => item.countsByStatus?.complete || 0,
  );
  const incompleteData = displayData.map(
    (item) => item.countsByStatus?.incomplete || 0,
  );

  const chartData = {
    labels,
    datasets: [
      {
        label: "Future",
        data: futureData,
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
      {
        label: "Complete",
        data: completeData,
        backgroundColor: "rgba(54, 162, 235, 0.6)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
      {
        label: "Incomplete",
        data: incompleteData,
        backgroundColor: "rgba(255, 206, 86, 0.6)",
        borderColor: "rgba(255, 206, 86, 1)",
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
      },
      y: {
        stacked: true,
        ticks: {
          autoSkip: false,
          font: {
            size: 10,
          },
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          footer: (tooltipItems) => {
            const total = tooltipItems.reduce(
              (sum, item) => sum + item.parsed.y,
              0,
            );
            return `Total: ${total}`;
          },
        },
      },
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const project = displayData[index];
        setSelectedProject(project);
        setShowModal(true);
      }
    },
  };

  return (
    <>
      <Paper sx={{ p: 2, height: "100%" }}>
        <Typography
          variant="h6"
          gutterBottom
        >
          Calls by Project (Top 15)
        </Typography>
        <Box sx={{ height: chartHeight }}>
          <Bar
            data={chartData}
            options={options}
          />
        </Box>
        {data.length > 15 && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowModal(true)}
            >
              View All ({data.length} projects)
            </Button>
          </Box>
        )}
      </Paper>

      {showModal && (
        <CallsDetailModal
          open={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedProject(null);
          }}
          filters={filters}
          projectId={selectedProject?.projectId}
          projectName={selectedProject?.projectName}
          callsDetail={callsDetail}
          getCalls={getCalls}
        />
      )}
    </>
  );
};

export default CallsByProjectChart;
